import Loader from '@/components/Loader';
import Constants from '@/Constants';
import { useCourseStudentView } from '@/hooks/useCourseStudentView';
import { ApplicationState } from '@/store';
import { updateCourseStudentViewRequest } from '@/store/list/action';
import { LocaleData } from '@/translations/LocaleData';
import { Action, Location } from 'history';
import React, { MutableRefObject, useEffect, useRef, useState } from 'react';
import Modal from 'react-bootstrap/esm/Modal';
import { useDispatch, useSelector } from 'react-redux';
import { Prompt, useParams } from 'react-router-dom';
import ConfirmDialog from '../ConfirmDialog';
import './style.scss';

const ScormPlayerConstants = {
    IFRAME_REGULAR_SCREEN_CLASSNAME: 'regularscreen',
    IFRAME_FULL_SCREEN_CLASSNAME: 'fullscreen',
};

interface Props {
    onFinish: () => void;
}

const ScormPlayer: React.FC<Props> = (props: Props) => {
    const { onFinish } = props;

    const courseId: number = Number(useParams<{ courseId: string }>().courseId);
    const classEnrollmentId: number = Number(useParams<{ enrollmentId: string }>().enrollmentId);

    const { urlScorm, personId, successOnUpdateCourseStudentView, courseStudentViewRedux } = useSelector((state: ApplicationState) => ({
        personId: state.identity.data.person.id,
        successOnUpdateCourseStudentView: state.lists.successOnUpdateCourseStudentView,
        urlScorm: state.lessons.mediaUrl,
        courseStudentViewRedux: state.lists.currentData,
    }));

    const strings: LocaleData = useSelector((state: ApplicationState) => state.locale.data);

    const { courseStudentView, mutate } = useCourseStudentView({
        moduleId: Constants.LMS_MODULE_ID,
        courseId,
        classEnrollmentId,
    });

    const dispatch = useDispatch();

    const [loading, setLoading] = useState<boolean>(true);
    const [bodyOverflow, setBodyOverflow] = useState<string>('');
    const [iframePlayerClassname, setIframePlayerClassname] = useState<string>(ScormPlayerConstants.IFRAME_REGULAR_SCREEN_CLASSNAME);
    const [show, setShow] = useState(false);
    const [fullSreenModal, setFullSreenModal] = useState(false);

    const iframePlayerRef: MutableRefObject<HTMLIFrameElement | null> = useRef<HTMLIFrameElement>(null);

    const callLmsFinish = () => {
        if (iframePlayerRef.current && iframePlayerRef.current.contentWindow) {
            iframePlayerRef.current.contentWindow.postMessage('callLmsFinish', '*');
            setLoading(true);
        }
    };

    useEffect(() => {
        if (urlScorm) {
            setShow(true);
        }
    }, [urlScorm]);

    const handlerMessage = (e: MessageEvent) => {
        if (e.data === 'callLmsFinishCallback') {
            if (personId && courseId) {
                dispatch(updateCourseStudentViewRequest({ personId, courseId, classEnrollmentId }));
            }
            onFinish();
        }
    };

    useEffect(() => {
        window.addEventListener('message', handlerMessage);
        return () => {
            window.removeEventListener('message', handlerMessage);
        };
    }, []);

    useEffect(() => {
        if (successOnUpdateCourseStudentView) {
            mutate({ ...courseStudentView, courseEnrollment: courseStudentViewRedux.courseEnrollment }, false);
            setShow(false);
        }
    }, [successOnUpdateCourseStudentView]);

    const onClickFullScreenButton = () => {
        const body: HTMLBodyElement = document.body as HTMLBodyElement;
        if (iframePlayerClassname === ScormPlayerConstants.IFRAME_REGULAR_SCREEN_CLASSNAME) {
            setIframePlayerClassname(ScormPlayerConstants.IFRAME_FULL_SCREEN_CLASSNAME);
            setBodyOverflow(body.style.overflow);
            body.style.overflow = 'hidden';
        } else {
            setIframePlayerClassname(ScormPlayerConstants.IFRAME_REGULAR_SCREEN_CLASSNAME);
            setIframePlayerClassname(ScormPlayerConstants.IFRAME_REGULAR_SCREEN_CLASSNAME);
            body.style.overflow = bodyOverflow;
        }

        setFullSreenModal(!fullSreenModal);
    };

    const [showWarningOnBack, setShowWarningOnBack] = useState<boolean>(false);

    return (
        <>
            <Prompt
                message={(location: Location, action: Action) => {
                    if (action === 'POP' && show) {
                        setShowWarningOnBack(true);
                        return false;
                    }
                    return true;
                }}
            />

            {showWarningOnBack && (
                <ConfirmDialog
                    content={strings.SCORM_CONFIRM_BACK_DIALOG.CONTENT}
                    rightButtonTitle={strings.SCORM_CONFIRM_BACK_DIALOG.BUTTON_LABEL}
                    rightClick={() => {
                        setShowWarningOnBack(false);
                        callLmsFinish();
                    }}
                    show
                    title={strings.SCORM_CONFIRM_BACK_DIALOG.TITLE}
                />
            )}

            <Modal dialogClassName="modal-customsize" onHide={callLmsFinish} show={show} size="xl">
                <Modal.Body>
                    {loading && <Loader />}
                    {urlScorm && (
                        <div className={`html-player ${iframePlayerClassname} ${loading ? 'hide' : ''}`}>
                            <div className="scorm-buttons-area">
                                <button onClick={onClickFullScreenButton}>
                                    <i className="material-icons">{fullSreenModal ? Constants.icons.FULLSCREEN_EXIT : Constants.icons.FULLSCREEN}</i>
                                </button>
                                <button onClick={callLmsFinish}>
                                    <i className="material-icons">{Constants.icons.CLOSE}</i>
                                </button>
                            </div>
                            <iframe
                                allowFullScreen={true}
                                className={iframePlayerClassname}
                                id="iframe-player"
                                onLoad={() => setLoading(false)}
                                ref={iframePlayerRef}
                                src={urlScorm}
                                style={{ height: 600 }}
                            />
                        </div>
                    )}
                </Modal.Body>
            </Modal>
        </>
    );
};

export default ScormPlayer;
